import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { expenseApi } from '../../services/api'
import MoveButtons from '../../components/common/MoveButtons'

const blank = { name: '', glAccount: '', sortOrder: 0, isActive: true, promptsTip: false, usesPlate: false, carWashCategoryId: '' }

export default function ExpenseCategories() {
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState(null)   // id, or 'new'
    const [draft, setDraft] = useState(blank)
    const [reordering, setReordering] = useState(false)

    useEffect(() => { load() }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await expenseApi.getCategories(true)
            setCategories(response.success ? response.categories || [] : [])
        } catch {
            toast.error('Could not load categories')
        }
        setLoading(false)
    }

    function startNew() {
        const nextSort = categories.reduce((max, c) => Math.max(max, c.sortOrder || 0), 0) + 10
        setDraft({ ...blank, sortOrder: nextSort })
        setEditing('new')
    }

    function startEdit(category) {
        setDraft({
            name: category.name,
            glAccount: category.glAccount || '',
            sortOrder: category.sortOrder || 0,
            isActive: category.isActive,
            promptsTip: category.promptsTip === true,
            usesPlate: category.usesPlate === true,
            carWashCategoryId: category.carWashCategoryId == null ? '' : String(category.carWashCategoryId),
        })
        setEditing(category.id)
    }

    async function save() {
        if (!draft.name.trim()) {
            toast.error('A name is required')
            return
        }

        const payload = {
            name: draft.name.trim(),
            glAccount: draft.glAccount.trim() || null,
            sortOrder: Number(draft.sortOrder) || 0,
            isActive: draft.isActive,
            promptsTip: draft.promptsTip,
            usesPlate: draft.usesPlate,
            carWashCategoryId: draft.carWashCategoryId ? Number(draft.carWashCategoryId) : null,
        }

        try {
            const response = editing === 'new'
                ? await expenseApi.createCategory(payload)
                : await expenseApi.updateCategory(editing, payload)

            if (response.success) {
                toast.success(editing === 'new' ? 'Category added' : 'Category updated')
                setEditing(null)
                load()
            } else {
                toast.error(response.message || 'Could not save')
            }
        } catch {
            toast.error('Could not save')
        }
    }

    /**
     * Moves a category one place up or down the list and saves the new order.
     *
     * The list is renumbered and shown straight away rather than after the round
     * trip: the arrow has to feel like it moved the row. If the save fails the
     * list is reloaded, so the screen ends up showing what is actually stored.
     */
    async function move(index, direction) {
        const target = index + direction
        if (target < 0 || target >= categories.length) return

        const reordered = [...categories]
        const [moved] = reordered.splice(index, 1)
        reordered.splice(target, 0, moved)

        // Tens, matching what the server stores, so the Order column does not
        // disagree with itself until the next load.
        setCategories(reordered.map((c, i) => ({ ...c, sortOrder: (i + 1) * 10 })))
        setReordering(true)

        try {
            const response = await expenseApi.reorderCategories(reordered.map(c => c.id))
            if (response.success) {
                if (response.categories) setCategories(response.categories)
            } else {
                toast.error(response.message || 'Could not save the new order')
                load()
            }
        } catch {
            toast.error('Could not save the new order')
            load()
        }
        setReordering(false)
    }

    const nameOf = id => categories.find(c => c.id === id)?.name || '—'

    async function hide(category) {
        if (!confirm(`Hide "${category.name}"?\n\nExisting expenses keep it, so past reports still read correctly. It just stops appearing on the entry form.`)) return

        try {
            const response = await expenseApi.deactivateCategory(category.id)
            if (response.success) {
                toast.success(response.message || 'Category hidden')
                load()
            } else {
                toast.error(response.message || 'Could not hide the category')
            }
        } catch {
            toast.error('Could not hide the category')
        }
    }

    return (
        <div>
            <div className="page-header flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="page-title">Expense Categories</h1>
                    <p className="text-blue-100 mt-1">
                        The list employees pick from, and the GL account each one posts to.
                    </p>
                </div>
                <Link to="/admin/expenses" className="btn btn-secondary">Back to Reports</Link>
            </div>

            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-700">
                        {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}
                    </h2>
                    <button className="btn btn-primary" onClick={startNew} disabled={editing === 'new'}>
                        Add Category
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
                ) : (
                    <>
                    {/* Phones: one card per category. Same treatment as the
                        expense lists - the table is fine on a desk, not on a phone. */}
                    <ul className="md:hidden divide-y">
                        {editing === 'new' && (
                            <li className="py-4 bg-blue-50 -mx-6 px-6">
                                <p className="font-semibold text-gray-800 mb-3">New category</p>
                                <DraftFields draft={draft} setDraft={setDraft} categories={categories} selfId={editing} />
                                <div className="flex gap-2 mt-4">
                                    <button className="btn btn-primary flex-1" onClick={save}>Save</button>
                                    <button className="btn btn-secondary flex-1" onClick={() => setEditing(null)}>Cancel</button>
                                </div>
                            </li>
                        )}

                        {categories.map(c => (
                            <li
                                key={c.id}
                                className={`py-4 ${editing === c.id ? 'bg-blue-50 -mx-6 px-6' : ''} ${c.isActive || editing === c.id ? '' : 'opacity-50'}`}
                            >
                                {editing === c.id ? (
                                    <>
                                        <p className="font-semibold text-gray-800 mb-3">Editing</p>
                                        <DraftFields draft={draft} setDraft={setDraft} categories={categories} selfId={editing} />
                                        <div className="flex gap-2 mt-4">
                                            <button className="btn btn-primary flex-1" onClick={save}>Save</button>
                                            <button className="btn btn-secondary flex-1" onClick={() => setEditing(null)}>Cancel</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-gray-900 break-words">{c.name}</p>
                                                <p className="text-sm text-gray-500">
                                                    {c.glAccount ? `GL ${c.glAccount}` : 'No GL account'} · order {c.sortOrder}
                                                    {c.promptsTip && ' · asks for a tip'}
                                                    {c.usesPlate && ' · asks for a plate'}
                                                    {c.carWashCategoryId != null && ` · car wash → ${nameOf(c.carWashCategoryId)}`}
                                                </p>
                                            </div>
                                            {c.isActive
                                                ? <span className="status-badge status-exported shrink-0">Shown</span>
                                                : <span className="status-badge status-pending shrink-0">Hidden</span>}
                                        </div>
                                        <div className="flex items-center gap-4 mt-3">
                                            <MoveButtons
                                                index={categories.indexOf(c)}
                                                count={categories.length}
                                                onMove={move}
                                                disabled={reordering || editing !== null}
                                            />
                                            <span className="flex-1" />
                                            <button className="text-rdi-blue text-sm font-medium" onClick={() => startEdit(c)}>
                                                Edit
                                            </button>
                                            {c.isActive && (
                                                <button className="text-rdi-red text-sm font-medium" onClick={() => hide(c)}>
                                                    Hide
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2" style={{ width: '28%' }}>Name</th>
                                    <th className="py-2" style={{ width: '15%' }}>GL Account</th>
                                    <th className="py-2" style={{ width: '14%' }}>Order</th>
                                    <th className="py-2" style={{ width: '10%' }}>Shown</th>
                                    <th className="py-2" style={{ width: '8%' }} title="Show a tip box on the entry form">Tip</th>
                                    <th className="py-2" style={{ width: '8%' }} title="Ask for a vehicle plate instead of a job number">Plate</th>
                                    <th className="py-2" style={{ width: '14%' }} title="Offer a car wash box, and file the wash under this category">Car wash</th>
                                    <th className="py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {editing === 'new' && (
                                    <DraftRow
                                        draft={draft}
                                        setDraft={setDraft}
                                        categories={categories}
                                        selfId={editing}
                                        onSave={save}
                                        onCancel={() => setEditing(null)}
                                    />
                                )}

                                {categories.map(c => (
                                    editing === c.id ? (
                                        <DraftRow
                                            key={c.id}
                                            draft={draft}
                                            setDraft={setDraft}
                                            categories={categories}
                                            selfId={editing}
                                            onSave={save}
                                            onCancel={() => setEditing(null)}
                                        />
                                    ) : (
                                        <tr key={c.id} className={`border-b border-gray-100 ${c.isActive ? '' : 'opacity-50'}`}>
                                            <td className="py-2 font-medium">{c.name}</td>
                                            <td className="py-2 text-gray-600">{c.glAccount || '—'}</td>
                                            <td className="py-2 text-gray-500">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-6">{c.sortOrder}</span>
                                                    <MoveButtons
                                                        index={categories.indexOf(c)}
                                                        count={categories.length}
                                                        onMove={move}
                                                        disabled={reordering || editing !== null}
                                                    />
                                                </div>
                                            </td>
                                            <td className="py-2">
                                                {c.isActive
                                                    ? <span className="status-badge status-exported">Yes</span>
                                                    : <span className="status-badge status-pending">Hidden</span>}
                                            </td>
                                            <td className="py-2 text-gray-600">{c.promptsTip ? 'Yes' : '—'}</td>
                                            <td className="py-2 text-gray-600">{c.usesPlate ? 'Yes' : '—'}</td>
                                            <td className="py-2 text-gray-600">
                                                {c.carWashCategoryId != null ? nameOf(c.carWashCategoryId) : '—'}
                                            </td>
                                            <td className="py-2 text-right whitespace-nowrap">
                                                <button
                                                    className="text-rdi-blue hover:underline mr-3"
                                                    onClick={() => startEdit(c)}
                                                >
                                                    Edit
                                                </button>
                                                {c.isActive && (
                                                    <button
                                                        className="text-rdi-red hover:underline"
                                                        onClick={() => hide(c)}
                                                    >
                                                        Hide
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}

                <p className="text-xs text-gray-500 mt-4">
                    The arrows set the order the categories appear in on the entry form, so the ones
                    used every day can sit at the top. Categories are never deleted, only hidden — an
                    expense already filed against one still needs its name when an old month is
                    reprinted. "Ask for a tip" adds a tip box
                    to the entry form for that category; the tip comes out of the amount paid before
                    the tax is worked out, since no tax is charged on a tip. "Ask for a plate" swaps the
                    job number box for the vehicle list — fuel belongs to a truck, not a work order.
                    "Car wash" adds a car wash box for that category: the receipt is still entered once,
                    and the report files the wash under the category picked here and the rest under this one.
                </p>
            </div>
        </div>
    )
}

/**
 * Which category a car wash on this one's receipts is filed under, or none.
 * Itself is left out - filing a wash under its own category would split nothing.
 */
function CarWashSelect({ draft, setDraft, categories, selfId }) {
    return (
        <select
            value={draft.carWashCategoryId}
            onChange={e => setDraft({ ...draft, carWashCategoryId: e.target.value })}
            title="Offer a car wash box, and file the wash under this category"
        >
            <option value="">No car wash</option>
            {categories
                .filter(c => c.id !== selfId && (c.isActive || String(c.id) === String(draft.carWashCategoryId)))
                .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
    )
}

/** The same fields as DraftRow, stacked and labelled, for phones. */
function DraftFields({ draft, setDraft, categories, selfId }) {
    return (
        <div className="space-y-3">
            <div>
                <label className="form-label">Name</label>
                <input
                    type="text"
                    autoFocus
                    value={draft.name}
                    onChange={e => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Fuel"
                />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="form-label">GL account</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={draft.glAccount}
                        onChange={e => setDraft({ ...draft, glAccount: e.target.value })}
                        placeholder="5705"
                    />
                </div>
                <div>
                    <label className="form-label">Order</label>
                    <input
                        type="number"
                        value={draft.sortOrder}
                        onChange={e => setDraft({ ...draft, sortOrder: e.target.value })}
                    />
                </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={draft.isActive}
                    onChange={e => setDraft({ ...draft, isActive: e.target.checked })}
                />
                Show on the entry form
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={draft.promptsTip}
                    onChange={e => setDraft({ ...draft, promptsTip: e.target.checked })}
                />
                Ask for a tip
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={draft.usesPlate}
                    onChange={e => setDraft({ ...draft, usesPlate: e.target.checked })}
                />
                Ask for a plate instead of a job number
            </label>
            <div>
                <label className="form-label">Car wash on the same receipt is filed under</label>
                <CarWashSelect draft={draft} setDraft={setDraft} categories={categories} selfId={selfId} />
            </div>
        </div>
    )
}

function DraftRow({ draft, setDraft, onSave, onCancel, categories, selfId }) {
    return (
        <tr className="border-b border-gray-100 bg-blue-50">
            <td className="py-2 pr-2">
                <input
                    type="text"
                    autoFocus
                    value={draft.name}
                    onChange={e => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Fuel"
                />
            </td>
            <td className="py-2 pr-2">
                <input
                    type="text"
                    value={draft.glAccount}
                    onChange={e => setDraft({ ...draft, glAccount: e.target.value })}
                    placeholder="5705"
                />
            </td>
            <td className="py-2 pr-2">
                <input
                    type="number"
                    value={draft.sortOrder}
                    onChange={e => setDraft({ ...draft, sortOrder: e.target.value })}
                />
            </td>
            <td className="py-2">
                <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={draft.isActive}
                    onChange={e => setDraft({ ...draft, isActive: e.target.checked })}
                />
            </td>
            <td className="py-2">
                <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={draft.promptsTip}
                    onChange={e => setDraft({ ...draft, promptsTip: e.target.checked })}
                    title="Show a tip box on the entry form"
                />
            </td>
            <td className="py-2">
                <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={draft.usesPlate}
                    onChange={e => setDraft({ ...draft, usesPlate: e.target.checked })}
                    title="Ask for a vehicle plate instead of a job number"
                />
            </td>
            <td className="py-2 pr-2">
                <CarWashSelect draft={draft} setDraft={setDraft} categories={categories} selfId={selfId} />
            </td>
            <td className="py-2 text-right whitespace-nowrap">
                <button className="text-rdi-blue font-medium hover:underline mr-3" onClick={onSave}>Save</button>
                <button className="text-gray-500 hover:underline" onClick={onCancel}>Cancel</button>
            </td>
        </tr>
    )
}
